import { confirmAction } from "../../components/ui/confirmAction";
import Pagination from "../../components/ui/Pagination";
import StatusAction from "../../components/ui/StatusAction";
import { getCustomerDisplayName } from "../../utils/customerDisplay";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./ProductsPage.css";

import {
  getFoodingOrders,
  updateFoodingOrderStatus,
} from "../../services/foodingOrderService";
import {
  getAllProducts,
  createProduct,
  deleteProduct,
  getProductApiErrorMessage,
} from "../../services/productService";
import {
  PRODUCT_STATUS_OPTIONS,
  formatProductStatus,
  normalizeProductStatusForApi,
} from "../../constants/productStatus";

import { Order } from "../../types/order";
import { Product } from "../../types/product";

import ProductFormModal from "../../compoonents/ProductFormModal/ProductFormModal";
import PageLoader from "../../compoonents/PageLoader";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";

// Icons
import { FiEye, FiTrash2 } from "react-icons/fi";


import defaultProductImage from "../../assets/images/empty.png";
import ProductImage from "../../compoonents/ProductImage/ProductImage";

type ProductTab = "products" | "orders";

// --- Enums and Status Lists ---
// Shipment statuses for Fooding product orders (EShipmentStatus — lowercase values)
const SHIPMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "in_transit", label: "In Transit" },
  { value: "shipped", label: "Shipped" },
  { value: "packed", label: "Packed" },
  { value: "checked_in", label: "Checked In" },
  { value: "checked_out", label: "Checked Out" },
  { value: "not_checked", label: "Not Checked" },
  { value: "arrived_at_local_hub", label: "Arrived At Local Hub" },
  { value: "arrived_at_nigeria_hub", label: "Arrived At Nigeria Hub" },
  { value: "arrived_at_uk_hub", label: "Arrived At UK Hub" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];

const formatShipmentStatus = (status: string): string => {
  const match = SHIPMENT_STATUS_OPTIONS.find(
    (o) => o.value.toLowerCase() === (status || "").toLowerCase(),
  );
  return match ? match.label : (status || "N/A").replace(/_/g, " ");
};

// --- Sub-Components ---

// Table status controls use the same keyboard-accessible dropdown as forms.
const OrderStatusDropdown: React.FC<{
  currentStatus: string;
  orderId: string;
  onStatusUpdate: (newStatus: string) => Promise<void>;
}> = ({ currentStatus, orderId, onStatusUpdate }) => {
  return <StatusAction value={currentStatus} label={formatShipmentStatus(currentStatus)} options={SHIPMENT_STATUS_OPTIONS}
    onChange={async status => {
      try { await onStatusUpdate(status); toast.success("Status updated"); }
      catch { toast.error("Failed to update status"); }
    }} />;
};

// --- Tab Components ---

const AllProductsTab: React.FC<{
  onAddProduct: () => void;
  refetchKey: number;
  searchTerm: string; // Recieved from parent
  filterStatus: string; // Recieved from parent
}> = ({ onAddProduct, refetchKey, searchTerm, filterStatus }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => setCurrentPage(1), [searchTerm, filterStatus]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Pass params to backend (if supported)
        const response = await getAllProducts(searchTerm, filterStatus);
        setProducts(response.data.products || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };

    // Debounce to prevent flashing
    const timer = setTimeout(() => fetchProducts(), 500);
    return () => clearTimeout(timer);
  }, [refetchKey, searchTerm, filterStatus]);

  // Client-side Fallback Filtering (Since getAllProducts returns array)
  // This ensures search works even if backend doesn't handle params
  const visibleProducts = useMemo(() => {
    return products.filter((p) => {
      const matchSearch = p.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchStatus = filterStatus
        ? normalizeProductStatusForApi(p.productStatus ?? "") === filterStatus
        : true;
      return matchSearch && matchStatus;
    });
  }, [products, searchTerm, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(visibleProducts.length / 10));
  const page = Math.min(currentPage, totalPages);
  const pageProducts = visibleProducts.slice((page - 1) * 10, page * 10);
  useEffect(() => setCurrentPage(value => Math.min(value, totalPages)), [totalPages]);

  const handleDelete = async (productId: string) => {
    if (await confirmAction("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        setProducts((curr) => curr.filter((p) => p.id !== productId));
        toast.success("Product deleted successfully");
      } catch (error: any) {
        const msg =
          error.response?.data?.message || "Failed to delete product.";
        toast.error(msg);
      }
    }
  };

  if (loading) return <PageLoader message="Loading products..." />;

  return (
    <div className="all-products-tab">
      <div className="list-header">
        <div /> {/* Spacer */}
        <button className="action-button primary" onClick={onAddProduct}>
          + Add New Product
        </button>
      </div>

      {visibleProducts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          {searchTerm || filterStatus
            ? "No products found matching criteria."
            : "No products available."}
        </div>
      ) : (
        <div className="table-container admin-table-container">
          <table className="data-table admin-data-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageProducts.map((product) => (
                <tr key={product.id}>
                  <td data-label="Image">
                    <ProductImage
                      paths={product.image}
                      alt={product.name}
                      className="product-image"
                      fallback={defaultProductImage}
                    />
                  </td>
                  <td data-label="Name">{product.name}</td>
                  <td data-label="Price">{`₦${Number(product.price).toLocaleString()}`}</td>
                  <td data-label="Quantity">{product.quantity}</td>
                  <td data-label="Status">
                    <span className={`status-badge status-${(product.productStatus || "").replace(/_/g, "-")}`}>{formatProductStatus(product.productStatus)}</span>
                  </td>
                  <td data-label="Action" className="action-cell center-text">
                    <div className="table-action-buttons">
                      <button
                        type="button"
                        className="table-action-btn"
                        title="View details"
                        aria-label="View details"
                        onClick={() =>
                          navigate(`/product-details/${product.id}`)
                        }
                      >
                        <FiEye />
                      </button>
                      <button
                        type="button"
                        className="table-action-btn danger"
                        title="Delete product"
                        aria-label="Delete product"
                        onClick={() => handleDelete(product.id)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages}
        totalItems={visibleProducts.length} onPageChange={setCurrentPage} label="Fooding products pagination" />}
    </div>
  );
};

const OrdersTab: React.FC<{
  searchTerm: string; // Recieved from parent
  filterStatus: string; // Recieved from parent
}> = ({ searchTerm, filterStatus }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    // Reset page to 1 if search/filter changes
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        // Pass params to backend
        const response = await getFoodingOrders(
          currentPage,
          10,
          searchTerm,
          filterStatus,
        );
        setOrders(response.data?.allOrders || []);
        setTotalPages(response.data?.pagination?.totalPages ?? 1);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => fetchOrders(), 500);
    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, filterStatus]);

  // Client-Side Fallback for the CURRENT PAGE data
  const visibleOrders = useMemo(() => {
    return orders.filter((o) => {
      const searchLower = searchTerm.toLowerCase();
      const name = getCustomerDisplayName(o.user).toLowerCase();
      const email = (o.user?.email ?? "").toLowerCase();
      const matchSearch =
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        o.id.includes(searchLower);

      const matchStatus = filterStatus
        ? ((o as any).shipmentStatus || o.status || "").toLowerCase() ===
          filterStatus.toLowerCase()
        : true;
      return matchSearch && matchStatus;
    });
  }, [orders, searchTerm, filterStatus]);

  if (loading) return <PageLoader message="Loading orders..." />;

  return (
    <>
      {visibleOrders.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          No orders found.
        </div>
      ) : (
        <div className="table-container admin-table-container">
          <table className="data-table admin-data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer Name</th>
                <th>Email</th>
                <th>Date Created</th>
                <th>Quantity</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((order) => (
                <tr key={order.id}>
                  <td data-label="Order ID">{order.id.substring(0, 8)}...</td>
                  <td data-label="Customer Name">
                    {getCustomerDisplayName(order.user)}
                  </td>
                  <td data-label="Email">{order.user?.email || "N/A"}</td>
                  <td data-label="Date Created">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td data-label="Quantity">{order.items?.length}</td>
                  <td data-label="Status">
                    <OrderStatusDropdown
                      currentStatus={
                        (order as any).shipmentStatus || order?.status
                      }
                      orderId={order.id}
                      onStatusUpdate={async (newStatus) => {
                        try {
                          await updateFoodingOrderStatus(order.id, newStatus);
                          setOrders(
                            orders.map((o) =>
                              o.id === order.id
                                ? ({
                                    ...o,
                                    shipmentStatus: newStatus,
                                    status: newStatus,
                                  } as any)
                                : o,
                            ),
                          );
                        } catch (error) {
                          throw error;
                        }
                      }}
                    />
                  </td>
                  <td data-label="Action">
                    <div className="table-action-buttons">
                      <button
                        type="button"
                        className="table-action-btn"
                        title="View order"
                        aria-label="View order"
                        onClick={() => navigate(`/fooding-orders/${order.id}`)}
                      >
                        <FiEye />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {visibleOrders.length > 0 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} loading={loading} />
      )}
    </>
  );
};

// --- Main Page Component ---
const ProductsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ProductTab>("products");
  const [showModal, setShowModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [forceRefetch, setForceRefetch] = useState(0);

  // STATE FOR SEARCH & FILTER
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Reset Search/Filter when changing Tabs
  useEffect(() => {
    setSearchTerm("");
    setFilterStatus("");
  }, [activeTab]);

  const handleCreateProduct = async (formData: FormData) => {
    setLoadingAction(true);
    try {
      await createProduct(formData);
      toast.success("Product created successfully!");
      setShowModal(false);
      setForceRefetch((count) => count + 1);
    } catch (error) {
      toast.error(getProductApiErrorMessage(error));
      console.error(error);
    } finally {
      setLoadingAction(false);
    }
  };

  const filterOptions =
    activeTab === "products" ? PRODUCT_STATUS_OPTIONS : SHIPMENT_STATUS_OPTIONS;

  return (
    <div className="products-page">

      <header className="page-header">
        <h3>Fooding</h3>
        <div className="page-controls">
          <PageToolbar>
            <PageFilterDropdown
              value={filterStatus}
              options={filterOptions}
              onChange={setFilterStatus}
            />
            <PageSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={
                activeTab === "products"
                  ? "Search products..."
                  : "Search orders..."
              }
            />
          </PageToolbar>
        </div>
      </header>

      <div className="page-content-wrapper">
        <div className="product-tabs">
          <button
            className={activeTab === "products" ? "active" : ""}
            onClick={() => setActiveTab("products")}
          >
            Fooding
          </button>
          <button
            className={activeTab === "orders" ? "active" : ""}
            onClick={() => setActiveTab("orders")}
          >
            Orders
          </button>
        </div>

        <div className="product-content">
          {activeTab === "products" ? (
            <AllProductsTab
              onAddProduct={() => setShowModal(true)}
              refetchKey={forceRefetch}
              searchTerm={searchTerm} // Pass down
              filterStatus={filterStatus} // Pass down
            />
          ) : (
            <OrdersTab
              searchTerm={searchTerm} // Pass down
              filterStatus={filterStatus} // Pass down
            />
          )}
        </div>
      </div>

      {showModal && (
        <ProductFormModal
          onClose={() => setShowModal(false)}
          onSave={handleCreateProduct}
          loading={loadingAction}
        />
      )}
    </div>
  );
};

export default ProductsPage;
